import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiCheckCircle, FiArrowRight, FiStar } = FiIcons;

const ProblemSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang].problem;
  const steps = stepsTranslations[lang];

  const displayTestimonials = t.testimonials.slice(0, 3);

  return (
    <div className="flex flex-col">
      {/* Problem Introduction */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              {t.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              {t.subtitle}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            {/* Visual Graphic: Chaos vs Clarity */}
            <div className="relative p-8 bg-gray-50 rounded-[3rem] border border-gray-100 shadow-inner">
              <div className="flex flex-col md:flex-row justify-between items-center gap-8 relative">

                {/* Chaos Side */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center text-center space-y-4 z-10"
                >
                  <div className="w-40 h-40 relative flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 border-4 border-dashed border-red-200 rounded-full"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      {[...Array(9)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={{
                            x: [0, Math.random() * 10 - 5, 0],
                            y: [0, Math.random() * 10 - 5, 0],
                            rotate: [0, Math.random() * 20 - 10, 0]
                          }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.1 }}
                          className="w-4 h-4 bg-red-400 rounded-sm"
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-red-600">{t.chaosLabel}</h4>
                    <p className="text-[10px] text-gray-400 max-w-[150px]">Focus lost in details & overwhelm</p>
                  </div>
                </motion.div>

                {/* Transformation Arrow */}
                <div className="hidden md:flex flex-col items-center">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: 100 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-1 bg-gradient-to-r from-red-200 via-blue-200 to-green-200 relative"
                  >
                    <SafeIcon icon={FiArrowRight} className="absolute -right-2 -top-1.5 text-green-500" />
                  </motion.div>
                </div>

                {/* Clarity Side */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center text-center space-y-4 z-10"
                >
                  <div className="w-40 h-40 relative flex items-center justify-center">
                    <motion.div className="absolute inset-0 border-4 border-green-100 rounded-full" />
                    <div className="flex flex-col space-y-2">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ width: 0 }}
                          whileInView={{ width: 40 + (i * 10) }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.8 + (i * 0.1) }}
                          className="h-2 bg-green-500 rounded-full"
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-green-600">{t.clarityLabel}</h4>
                    <p className="text-[10px] text-gray-400 max-w-[150px]">Structured steps to success</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Path description */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiAlertCircle} className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.overwhelmedTitle}</h3>
                  <p className="text-gray-600 leading-relaxed">{t.overwhelmedDesc}</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.solutionTitle}</h3>
                  <p className="text-gray-600 leading-relaxed">{t.solutionDesc}</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="py-16 bg-blue-50/30 rounded-[3rem] px-6 mb-24 relative overflow-hidden border border-blue-50">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 opacity-5">
              <SafeIcon icon={FiStar} className="text-[200px] text-blue-900" />
            </div>

            <div className="text-center mb-12 relative z-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
              >
                {t.testimonialsTitle}
              </motion.h3>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">{t.testimonialsSubtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10 max-w-6xl mx-auto">
              {displayTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-blue-100/50 flex flex-col h-full transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="flex items-center space-x-0.5 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-[10px] fill-yellow-400" />
                    ))}
                  </div>

                  <div className="flex-grow space-y-3">
                    <div className="relative">
                      <p className="text-[9px] font-black text-red-500 uppercase tracking-widest mb-1 opacity-70">Vorher</p>
                      <p className="text-gray-500 italic text-[11px] leading-relaxed">"{testimonial.before}"</p>
                    </div>
                    <div className="w-4 h-px bg-blue-50" />
                    <div className="relative">
                      <p className="text-[9px] font-black text-green-600 uppercase tracking-widest mb-1 opacity-70">Mit Stup-On</p>
                      <p className="text-gray-800 font-semibold text-[13px] leading-snug">"{testimonial.after}"</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-50 flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-[10px] shadow-sm">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-xs">{testimonial.name}</p>
                      <p className="text-[9px] text-gray-400 font-medium">{testimonial.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Methodology Summary */}
          <div className="mt-20 pt-20 border-t border-gray-100">
            <div className="text-center mb-16">
              <p style={{ color: 'rgb(75, 85, 99)', fontSize: '21px' }} className="text-gray-600 text-lg max-w-2xl mx-auto">
                {t.methodologySubtitle}
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative group"
                  >
                    <div className="bg-white border border-gray-100 rounded-3xl p-6 h-full transition-all duration-300 group-hover:border-blue-500 group-hover:shadow-xl group-hover:-translate-y-2 flex flex-col items-center text-center">
                      <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-black mb-4 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm">
                        {index + 1}
                      </div>
                      <h4 className="font-bold text-gray-900 text-[11px] uppercase tracking-wider mb-2">{step.title}</h4>
                      <div className="w-6 h-1 bg-blue-100 group-hover:w-12 group-hover:bg-blue-500 transition-all duration-300 rounded-full" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProblemSection;